import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { programAutomationActions, RequestStatus } from "@fitness-app/app-store";
import { AppAccessType, FitnessAppTask } from "@fitness-app/data-models/entities/Automation";
import { CustomerFeatureName } from "@fitness-app/data-models/entities/CustomerFeature";
import { ProductType, type Product } from "@fitness-app/data-models/entities/Product";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

export const useFitnessAppAutomationTask = (product: Product | null) => {
  const { t } = useTranslation(["common", "products"]);
  const { listStatus, data } = useAppSelector((store) => store.programAutomation);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (listStatus !== RequestStatus.SUCCESS) {
      void dispatch(programAutomationActions.fetchProgramAutomations());
    }
  }, [dispatch]);

  const customerFeatures = useMemo(() => {
    return Object.values(CustomerFeatureName).map((feature) => ({
      value: feature,
      label: t(`common:customerFeatures.${feature}`),
    }));
  }, [t]);

  const fitnessAppTaskOptions = useMemo(() => {
    if (!product || product.type === ProductType.ONLY_CONTENT || product.type === ProductType.EVENT) {
      return [];
    }

    if (product.type === ProductType.ADDON) {
      return [
        {
          label: t(`common:fitnessAppTask.${FitnessAppTask.AddProgramAutomationToClient}`),
          value: FitnessAppTask.AddProgramAutomationToClient,
        },
        {
          label: t(`common:fitnessAppTask.${FitnessAppTask.ActivatedCustomerFeature}`),
          value: FitnessAppTask.ActivatedCustomerFeature,
        },
        {
          label: t(`common:fitnessAppTask.${FitnessAppTask.DeactivatedCustomerFeature}`),
          value: FitnessAppTask.DeactivatedCustomerFeature,
        },
        {
          label: t(`common:fitnessAppTask.${FitnessAppTask.AddTagsToTraineeProfile}`),
          value: FitnessAppTask.AddTagsToTraineeProfile,
        },
        {
          label: t(`common:fitnessAppTask.${FitnessAppTask.AddTagsToTraineeProfile}`),
          value: FitnessAppTask.RemoveTagsFromTraineeProfile,
        },
      ];
    }

    return [
      FitnessAppTask.AddClientToApp,
      FitnessAppTask.RemoveClientFromApp,
      FitnessAppTask.ActivatedCustomerFeature,
      FitnessAppTask.DeactivatedCustomerFeature,
      FitnessAppTask.AddTagsToTraineeProfile,
      FitnessAppTask.RemoveTagsFromTraineeProfile,
    ].map((value) => ({
      label: t(`common:fitnessAppTask.${value}`),
      value,
    }));
  }, [t]);

  const programAutomationOptions = useMemo(() => {
    return data.map((automation) => ({
      label: automation.name,
      value: automation.id,
    }));
  }, [data]);

  // An automation that builds AI plans from the initial survey has nothing to build them from
  // at purchase: started "after purchase", the diet is skipped and the training ignores the
  // answers. The form warns about that combination.
  const surveyDrivenAutomationIds = useMemo(
    () =>
      data
        .filter((automation) => {
          const aiPlans = [automation.aiNutritionPlan, automation.aiTrainingProgram].flat();
          return Boolean(automation.initialSurveyId) && aiPlans.some((config) => config?.enabled);
        })
        .map((automation) => automation.id),
    [data],
  );

  const appAccessOptions = useMemo(() => {
    return Object.values(AppAccessType).map((access) => ({
      value: access,
      label: t(`accessType.${access}`),
    }));
  }, [t, product]);

  return {
    fitnessAppTaskOptions,
    programAutomationOptions,
    surveyDrivenAutomationIds,
    appAccessOptions,
    customerFeatures,
  };
};
